import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import moment from 'moment';
import { Notification } from 'services';
import { getAddressFromPin } from 'pages/profile/postalApi';
import { mobileDigitsOf, isMobileComplete, formatDob } from './utils';

export const useProfileDetails = ({
    userId,
    userRowDetails,
    isStaffRoute,
    basePath,
    history,
    actions: {
        getStaffProfile,
        getStudentProfile,
        updateStaffProfile,
        updateStudentProfile,
        uploadStaffPhoto,
        uploadStudentPhoto,
        deleteStaffPhoto,
        deleteStudentPhoto,
        updateStaffStatusV3,
        updateStudentStatus,
        getStaffListV3,
        getStaffStats,
    }
}) => {
    const [editingSection, setEditingSection] = useState(null);
    const [draftProfile, setDraftProfile] = useState({});
    const [savingSection, setSavingSection] = useState(false);

    // Profile photo state
    const inputProfilePicRef = useRef(null);
    const [profilePicModalOpen, setProfilePicModalOpen] = useState(false);
    const [imgUrl, setImgUrl] = useState(null); // confirmed photo blob URL
    const [imageFile, setImageFile] = useState(null); // confirmed File object
    const [stagedImgUrl, setStagedImgUrl] = useState(null); // staging inside modal
    const [stagedImageFile, setStagedImageFile] = useState(null); // staging inside modal
    const [pendingPhotoRemoval, setPendingPhotoRemoval] = useState(false);
    const [avatarHovered, setAvatarHovered] = useState(false);

    const prevContactPinRef = useRef(null);
    const prevPermanentPinRef = useRef(null);
    // Draft as it was when editing started — on save, only fields that
    // differ from it are sent.
    const initialDraftRef = useRef({});

    // Profile data state
    const [staffProfile, setStaffProfile] = useState(null);
    const [studentProfile, setStudentProfile] = useState(null);
    const [studentSidebar, setStudentSidebar] = useState(null);
    const specificUserProfile = isStaffRoute ? staffProfile : studentProfile;
    const [loading, setLoading] = useState(true);
    const [pageLoader, setPageLoader] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'suspend' | 'activate' | 'delete'
    const [confirmLoading, setConfirmLoading] = useState(false);

    const currentAddress = useMemo(() => {
        try {
            return JSON.parse(specificUserProfile?.basicprofile?.currentAddress || "{}");
        } catch (e) {
            return {};
        }
    }, [specificUserProfile]);

    const permanentAddress = useMemo(() => {
        try {
            return JSON.parse(specificUserProfile?.basicprofile?.permanentAddress || "{}");
        } catch (e) {
            return {};
        }
    }, [specificUserProfile]);

    const status = specificUserProfile?.status || userRowDetails?.status || "Active";
    const isActive = status === "Active";

    const rawRole = specificUserProfile?.role ?? userRowDetails?.role;
    const roleStr = Array.isArray(rawRole) ? (rawRole[0] || "") : (rawRole || "");
    const isTeacher = String(roleStr).toLowerCase() === "teacher";
    const isOperation = String(roleStr).toLowerCase().includes("operation");
    const isStaff = isStaffRoute || isTeacher || isOperation;
    const showAttendanceBySubject = isStaffRoute ? isTeacher : true;
    const visibleMonthColumns = showAttendanceBySubject ? 3 : 5;

    const handleEditSection = (sectionName) => {
        setEditingSection(sectionName);
        if (sectionName === 'Contact') {
            prevContactPinRef.current = currentAddress?.pincode ? String(currentAddress.pincode).trim() : null;
        }
        if (sectionName === 'PermanentAddress') {
            prevPermanentPinRef.current = permanentAddress?.pincode ? String(permanentAddress.pincode).trim() : null;
        }
        const initialDraft = {
            dob: specificUserProfile?.basicprofile?.dob ? moment(specificUserProfile.basicprofile.dob).format("YYYY-MM-DD") : "",
            gender: specificUserProfile?.basicprofile?.gender || "",
            bloodGroup: specificUserProfile?.basicprofile?.bloodGroup || "",
            phone: specificUserProfile?.phone || "",
            email: specificUserProfile?.email || "",
            currentHouseNo: currentAddress?.h_no || "",
            currentPincode: currentAddress?.pincode || "",
            currentDistrict: currentAddress?.city || "",
            currentState: currentAddress?.state || "",
            currentCountry: currentAddress?.country || "",
            permanentHouseNo: permanentAddress?.h_no || "",
            permanentPincode: permanentAddress?.pincode || "",
            permanentDistrict: permanentAddress?.city || "",
            permanentState: permanentAddress?.state || "",
            permanentCountry: permanentAddress?.country || "",
            fatherName: specificUserProfile?.basicprofile?.fatherName || "",
            fatherPhone: specificUserProfile?.basicprofile?.fatherPhone || "",
            fatherEmail: specificUserProfile?.basicprofile?.fatherEmail || "",
            motherName: specificUserProfile?.basicprofile?.motherName || "",
            motherPhone: specificUserProfile?.basicprofile?.motherPhone || "",
            motherEmail: specificUserProfile?.basicprofile?.motherEmail || "",
            emergencyContactName: specificUserProfile?.basicprofile?.emergencyContactName || "",
            emergencyContactPhone: (!specificUserProfile?.basicprofile?.emergencyPhone || specificUserProfile?.basicprofile?.emergencyPhone === 'NA')
                ? "+91"
                : (String(specificUserProfile.basicprofile.emergencyPhone).startsWith('+')
                    ? specificUserProfile.basicprofile.emergencyPhone
                    : `+91${specificUserProfile.basicprofile.emergencyPhone}`),
            emergencyContactRelationship: specificUserProfile?.basicprofile?.relationship || "",
        };
        initialDraftRef.current = initialDraft;
        setDraftProfile(initialDraft);
    };

    const handleDraftChange = (field, value) => {
        setDraftProfile(prev => {
            const normalizedValue = value === undefined ? "" : value;
            const updated = { ...prev, [field]: normalizedValue };
            // Editing a pincode clears its "not found" flag until the next lookup.
            if (field === 'currentPincode') updated.currentPincodeNotFound = false;
            if (field === 'permanentPincode') updated.permanentPincodeNotFound = false;
            // Pincode emptied, or a full 6-digit pincode lost a digit: the
            // looked-up district / state / country no longer apply.
            const pinShrunk = (prevPin, nextPin) => !nextPin || (prevPin.length === 6 && nextPin.length < 6);
            if (field === 'currentPincode' && pinShrunk(String(prev.currentPincode || '').trim(), String(normalizedValue || '').trim())) {
                updated.currentDistrict = '';
                updated.currentState = '';
                updated.currentCountry = '';
            }
            if (field === 'permanentPincode' && pinShrunk(String(prev.permanentPincode || '').trim(), String(normalizedValue || '').trim())) {
                updated.permanentDistrict = '';
                updated.permanentState = '';
                updated.permanentCountry = '';
            }
            if (field === 'emergencyContactName' || field === 'emergencyContactPhone') {
                const name = updated.emergencyContactName;
                const phone = updated.emergencyContactPhone;
                const isNameGiven = Boolean(name && String(name).trim().length > 0 && name !== 'NA');
                const isPhoneGiven = isMobileComplete(phone);
                if (!isNameGiven || !isPhoneGiven) {
                    updated.emergencyContactRelationship = '';
                }
            }
            return updated;
        });
    };

    useEffect(() => {
        if (editingSection !== 'Contact') {
            prevContactPinRef.current = null;
            return;
        }
        const pincode = draftProfile.currentPincode ? String(draftProfile.currentPincode).trim() : "";
        if (!pincode) {
            if (prevContactPinRef.current) {
                setDraftProfile(prev => ({
                    ...prev,
                    currentDistrict: '',
                    currentState: '',
                    currentCountry: '',
                }));
            }
            prevContactPinRef.current = "";
            return;
        }
        if (pincode.length !== 6) {
            prevContactPinRef.current = pincode;
            return;
        }
        if (pincode === prevContactPinRef.current) return;
        prevContactPinRef.current = pincode;

        getAddressFromPin(pincode, (district, state, country) => {
            setDraftProfile(prev => (String(prev.currentPincode || '').trim() === pincode
                // No post office for this pincode ("No records found") -> invalid.
                ? { ...prev, currentDistrict: district, currentState: state, currentCountry: country, currentPincodeNotFound: !district && !state }
                : prev));
        });
    }, [editingSection, draftProfile.currentPincode]);

    useEffect(() => {
        if (editingSection !== 'PermanentAddress') {
            prevPermanentPinRef.current = null;
            return;
        }
        const pincode = draftProfile.permanentPincode ? String(draftProfile.permanentPincode).trim() : "";
        if (!pincode) {
            if (prevPermanentPinRef.current) {
                setDraftProfile(prev => ({
                    ...prev,
                    permanentDistrict: '',
                    permanentState: '',
                    permanentCountry: '',
                }));
            }
            prevPermanentPinRef.current = "";
            return;
        }
        if (pincode.length !== 6) {
            prevPermanentPinRef.current = pincode;
            return;
        }
        if (pincode === prevPermanentPinRef.current) return;
        prevPermanentPinRef.current = pincode;

        getAddressFromPin(pincode, (district, state, country) => {
            setDraftProfile(prev => (String(prev.permanentPincode || '').trim() === pincode
                // No post office for this pincode ("No records found") -> invalid.
                ? { ...prev, permanentDistrict: district, permanentState: state, permanentCountry: country, permanentPincodeNotFound: !district && !state }
                : prev));
        });
    }, [editingSection, draftProfile.permanentPincode]);

    const handleCancelSection = () => {
        setEditingSection(null);
        prevContactPinRef.current = null;
        prevPermanentPinRef.current = null;
        setDraftProfile({});
        setImgUrl(null);
        setImageFile(null);
        setPendingPhotoRemoval(false);
        setStagedImgUrl(null);
        setStagedImageFile(null);
    };

    const refetchProfile = useCallback((onDone) => {
        if (!userId) {
            onDone?.();
            return;
        }
        if (isStaffRoute) {
            getStaffProfile(userId, (response) => {
                if (response) {
                    const profile = response?.data ?? response;
                    setStaffProfile({
                        ...profile,
                        status: profile?.status === "Suspended" ? "Inactive" : "Active",
                        role: profile?.roles?.[0]?.role || profile?.roles?.[0] || profile?.role,
                        privilage: Array.isArray(profile?.privilages) ? profile.privilages[0] : profile?.privilages,
                        quickStats: profile?.quickStats ?? response?.quickStats,
                        loginAndAccount: profile?.loginAndAccount ?? response?.loginAndAccount,
                        basicprofile: {
                            dob: profile?.dob,
                            gender: profile?.gender,
                            bloodGroup: profile?.bloodGroup,
                            emergencyContactName: profile?.emergencyContactName,
                            emergencyPhone: profile?.emergencyContactPhone,
                            relationship: profile?.emergencyContactRelationship,
                            currentAddress: JSON.stringify({
                                h_no: profile?.currentHouseNo,
                                city: profile?.currentDistrict,
                                state: profile?.currentState,
                                country: profile?.currentCountry,
                                pincode: profile?.currentPincode,
                            }),
                            permanentAddress: JSON.stringify({
                                h_no: profile?.permanentHouseNo,
                                city: profile?.permanentDistrict,
                                state: profile?.permanentState,
                                country: profile?.permanentCountry,
                                pincode: profile?.permanentPincode,
                            }),
                        },
                    });
                }
                onDone?.();
            });
        } else {
            getStudentProfile(userId, (response) => {
                if (response) {
                    const profile = response?.data ?? response;
                    setStudentProfile({
                        ...profile,
                        status: profile?.status === "Suspended" ? "Inactive" : "Active",
                        quickStats: profile?.quickStats ?? response?.quickStats,
                        loginAndAccount: profile?.loginAndAccount ?? response?.loginAndAccount,
                        feeReminder: profile?.feeReminder ?? response?.feeReminder,
                        basicprofile: {
                            dob: profile?.dob,
                            gender: profile?.gender,
                            bloodGroup: profile?.bloodGroup,
                            fatherName: profile?.fatherName,
                            motherName: profile?.motherName,
                            fatherPhone: profile?.fatherPhone,
                            fatherEmail: profile?.fatherEmail,
                            motherPhone: profile?.motherPhone,
                            motherEmail: profile?.motherEmail,
                            emergencyContactName: profile?.emergencyContactName,
                            emergencyPhone: profile?.emergencyContactPhone,
                            relationship: profile?.emergencyContactRelationship,
                            currentAddress: JSON.stringify({
                                h_no: profile?.currentHouseNo,
                                city: profile?.currentDistrict,
                                state: profile?.currentState,
                                country: profile?.currentCountry,
                                pincode: profile?.currentPincode,
                            }),
                            permanentAddress: JSON.stringify({
                                h_no: profile?.permanentHouseNo,
                                city: profile?.permanentDistrict,
                                state: profile?.permanentState,
                                country: profile?.permanentCountry,
                                pincode: profile?.permanentPincode,
                            }),
                        },
                    });
                    setStudentSidebar({
                        quickStats: profile?.quickStats ?? response?.quickStats,
                        loginAndAccount: profile?.loginAndAccount ?? response?.loginAndAccount,
                        feeReminder: profile?.feeReminder ?? response?.feeReminder,
                    });
                }
                onDone?.();
            });
        }
    }, [userId, isStaffRoute, getStaffProfile, getStudentProfile]);

    const handleSaveSection = () => {
        if (!userId) return Notification.error("Error", "No user ID found.");

        setSavingSection(true);

        // Same field mapping for the starting draft and the edited one, so the
        // two can be compared field by field.
        const buildParams = (draft) => {
            const nameParam = (editingSection === "BANNER" && draft.name !== undefined)
                ? (draft.name ? String(draft.name).trim() : "")
                : specificUserProfile?.name;

            const emergencyDigits = mobileDigitsOf(draft.emergencyContactPhone);
            const fatherPhoneDigits = mobileDigitsOf(draft.fatherPhone);
            const motherPhoneDigits = mobileDigitsOf(draft.motherPhone);

            const sharedParams = {
                name: nameParam,
                phone: draft.phone ? String(draft.phone).trim() : "",
                email: draft.email != null ? String(draft.email).trim() : "",
                dob: draft.dob ? formatDob(draft.dob) : "",
                gender: draft.gender != null ? String(draft.gender).trim() : "",
                bloodGroup: draft.bloodGroup != null ? String(draft.bloodGroup).trim() : "",
                currentHouseNo: draft.currentHouseNo != null ? String(draft.currentHouseNo).trim() : "",
                currentDistrict: draft.currentDistrict != null ? String(draft.currentDistrict).trim() : "",
                currentState: draft.currentState != null ? String(draft.currentState).trim() : "",
                currentCountry: draft.currentCountry != null ? String(draft.currentCountry).trim() : "",
                currentPincode: draft.currentPincode != null ? String(draft.currentPincode).trim() : "",
                permanentHouseNo: draft.permanentHouseNo != null ? String(draft.permanentHouseNo).trim() : "",
                permanentDistrict: draft.permanentDistrict != null ? String(draft.permanentDistrict).trim() : "",
                permanentState: draft.permanentState != null ? String(draft.permanentState).trim() : "",
                permanentCountry: draft.permanentCountry != null ? String(draft.permanentCountry).trim() : "",
                permanentPincode: draft.permanentPincode != null ? String(draft.permanentPincode).trim() : "",
                emergencyContactName: draft.emergencyContactName != null ? String(draft.emergencyContactName).trim() : "",
                emergencyContactPhone: emergencyDigits.length === 10
                    ? draft.emergencyContactPhone
                    : (emergencyDigits.length > 0 ? draft.emergencyContactPhone : ""),
                emergencyContactRelationship: draft.emergencyContactRelationship != null ? String(draft.emergencyContactRelationship).trim() : "",
            };

            return isStaffRoute ? {
                ...sharedParams,
                roles: (editingSection === "BANNER" && Array.isArray(draft.roles))
                    ? draft.roles
                    : (Array.isArray(specificUserProfile?.role) ? specificUserProfile?.role : (specificUserProfile?.role ? [specificUserProfile?.role] : [])),
                designation: specificUserProfile?.designation,
                employeeId: specificUserProfile?.employeeId,
            } : {
                ...sharedParams,
                fatherName: draft.fatherName != null ? String(draft.fatherName).trim() : "",
                motherName: draft.motherName != null ? String(draft.motherName).trim() : "",
                fatherPhone: fatherPhoneDigits.length > 0 ? draft.fatherPhone : "",
                fatherEmail: draft.fatherEmail != null ? String(draft.fatherEmail).trim() : "",
                motherPhone: motherPhoneDigits.length > 0 ? draft.motherPhone : "",
                motherEmail: draft.motherEmail != null ? String(draft.motherEmail).trim() : "",
            };
        };

        const initialParams = buildParams(initialDraftRef.current);
        const editedParams = buildParams(draftProfile);
        // Only the fields the user actually changed (a cleared field is sent as "").
        const finalParams = Object.fromEntries(
            Object.entries(editedParams).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(initialParams[k]))
        );

        const handleResponse = (res, error) => {
            if (res && !error) {
                const finishSave = () => {
                    refetchProfile(() => {
                        Notification.success("Success", "Profile updated successfully");
                        setEditingSection(null);
                        setSavingSection(false);
                        setImgUrl(null);
                        setImageFile(null);
                        setPendingPhotoRemoval(false);
                        setStagedImgUrl(null);
                        setStagedImageFile(null);
                    });
                };
                if (editingSection === 'BANNER' && imageFile) {
                    const uploadPhoto = isStaffRoute ? uploadStaffPhoto : uploadStudentPhoto;
                    // uploadAndPost reports 100 when the bytes are sent and
                    // again as (100, response) once the server replies — finish
                    // only once, on the server's reply (else the refetch and
                    // success snackbar ran twice).
                    let finished = false;
                    uploadPhoto(userId, imageFile, (percent, response) => {
                        if (finished) return;
                        if (percent === 100 && response) {
                            finished = true;
                            finishSave();
                        } else if (percent === -1) {
                            finished = true;
                            setSavingSection(false);
                        }
                    });
                } else if (editingSection === 'BANNER' && pendingPhotoRemoval) {
                    const deletePhoto = isStaffRoute ? deleteStaffPhoto : deleteStudentPhoto;
                    deletePhoto(userId, finishSave);
                } else {
                    finishSave();
                }
            } else {
                Notification.error("Error", error?.show?.message || error?.message || "Failed to update profile");
                setSavingSection(false);
            }
        };

        if (Object.keys(finalParams).length === 0) {
            // No profile field changed — skip the update call (a banner photo
            // change is still uploaded / removed by handleResponse).
            handleResponse({}, null);
        } else if (isStaffRoute) {
            updateStaffProfile(userId, finalParams, handleResponse);
        } else {
            updateStudentProfile(userId, finalParams, handleResponse);
        }
    };

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            setPageLoader(false);
            return;
        }
        setLoading(true);
        setPageLoader(true);
        refetchProfile(() => {
            setLoading(false);
            setPageLoader(false);
        });
    }, [userId, refetchProfile]);

    const handleToggleStatus = () => {
        if (!userId) return;
        setConfirmAction(isActive ? "suspend" : "activate");
    };

    const handleDelete = () => {
        if (!userId) return;
        setConfirmAction("delete");
    };

    const closeConfirmModal = () => {
        if (confirmLoading) return;
        setConfirmAction(null);
    };

    const handleConfirmAction = () => {
        if (!userId || !confirmAction) return;
        setConfirmLoading(true);

        const onFinish = (response, error) => {
            setConfirmLoading(false);
            if (error) {
                return;
            }
            setConfirmAction(null);
            refetchProfile();
        };

        if (confirmAction === "delete") {
            if (isStaffRoute) {
                updateStaffStatusV3(userId, "Deleted", (res, err) => {
                    setConfirmLoading(false);
                    if (err) {
                        return;
                    }
                    setConfirmAction(null);
                    getStaffStats?.();
                    getStaffListV3?.({ page: 1, limit: 1000, sort: "newest", sortBy: "newest" });
                    history.push({
                        pathname: basePath,
                        state: { deletedStaffId: userId }
                    });
                });
            } else {
                updateStudentStatus(userId, "Deleted", (res, err) => {
                    setConfirmLoading(false);
                    if (err) {
                        return;
                    }
                    setConfirmAction(null);
                    history.goBack();
                });
            }
        } else if (confirmAction === "suspend") {
            if (isStaffRoute) {
                updateStaffStatusV3(userId, "Suspended", onFinish);
            } else {
                updateStudentStatus(userId, "Suspended", onFinish);
            }
        } else if (confirmAction === "activate") {
            if (isStaffRoute) {
                updateStaffStatusV3(userId, "Approved", onFinish);
            } else {
                updateStudentStatus(userId, "Approved", onFinish);
            }
        }
    };

    const handleEditClick = () => {
        setEditingSection("BANNER");
        const initialDraft = {
            name: specificUserProfile?.name || userRowDetails?.name || "",
            phone: specificUserProfile?.phone || userRowDetails?.phone || "",
            email: specificUserProfile?.email || userRowDetails?.email || "",
            roles: Array.isArray(specificUserProfile?.role) ? specificUserProfile?.role : (specificUserProfile?.role ? [specificUserProfile?.role] : []),
        };
        initialDraftRef.current = initialDraft;
        setDraftProfile(initialDraft);
        setImgUrl(null);
        setImageFile(null);
        setPendingPhotoRemoval(false);
        setStagedImgUrl(null);
        setStagedImageFile(null);
    };

    const handleAvatarClick = () => {
        if (editingSection !== 'BANNER') return;
        setStagedImgUrl(null);
        setStagedImageFile(null);
        if (inputProfilePicRef.current) inputProfilePicRef.current.value = null;
        setProfilePicModalOpen(true);
    };

    const handleProfilePicCancel = () => {
        setProfilePicModalOpen(false);
        setStagedImgUrl(null);
        setStagedImageFile(null);
        if (inputProfilePicRef.current) inputProfilePicRef.current.value = null;
    };

    const handleProfilePicModalOk = () => {
        inputProfilePicRef?.current?.click();
    };

    const storeImageFile = (file) => {
        setStagedImageFile(file);
    };

    const handleConfirmPhotoChoice = (callback, buttonCallback) => {
        if (stagedImageFile) {
            setImgUrl(stagedImgUrl);
            setImageFile(stagedImageFile);
        }
        setPendingPhotoRemoval(false);
        setStagedImgUrl(null);
        setStagedImageFile(null);
        if (inputProfilePicRef.current) inputProfilePicRef.current.value = null;
        callback?.();
        buttonCallback?.();
        setProfilePicModalOpen(false);
    };

    const hasInitialPhoto = Boolean(
        specificUserProfile?.image &&
        specificUserProfile?.image !== "default.png" &&
        !String(specificUserProfile?.image).includes("default.png")
    );

    const handleRemovePhotoConfirm = () => {
        setPendingPhotoRemoval(hasInitialPhoto ? true : false);
        setImgUrl(null);
        setImageFile(null);
        setStagedImgUrl(null);
        setStagedImageFile(null);
        if (inputProfilePicRef.current) inputProfilePicRef.current.value = null;
        setProfilePicModalOpen(false);
    };

    const existingPhotoSrc = pendingPhotoRemoval
        ? ""
        : (imgUrl || (hasInitialPhoto ? specificUserProfile?.image : ""));

    const editingStaffForDrawer = useMemo(() => ({
        id: userId,
        name: specificUserProfile?.name,
        phone: specificUserProfile?.phone,
        email: specificUserProfile?.email,
        role: specificUserProfile?.role,
        access: specificUserProfile?.privilage,
        image: specificUserProfile?.image || specificUserProfile?.avatar,
    }), [userId, specificUserProfile]);

    return {
        editingSection,
        setEditingSection,
        draftProfile,
        setDraftProfile,
        savingSection,
        staffProfile,
        studentProfile,
        studentSidebar,
        specificUserProfile,
        loading,
        pageLoader,
        setPageLoader,
        editOpen,
        setEditOpen,
        currentAddress,
        permanentAddress,
        status,
        isActive,
        roleStr,
        isTeacher,
        isOperation,
        isStaff,
        showAttendanceBySubject,
        visibleMonthColumns,
        editingStaffForDrawer,
        inputProfilePicRef,
        profilePicModalOpen,
        setProfilePicModalOpen,
        imgUrl,
        setImgUrl,
        imageFile,
        setImageFile,
        stagedImgUrl,
        setStagedImgUrl,
        stagedImageFile,
        setStagedImageFile,
        existingPhotoSrc,
        handleProfilePicCancel,
        pendingPhotoRemoval,
        avatarHovered,
        setAvatarHovered,
        handleEditSection,
        handleDraftChange,
        handleCancelSection,
        refetchProfile,
        handleSaveSection,
        handleToggleStatus,
        handleDelete,
        confirmAction,
        confirmLoading,
        closeConfirmModal,
        handleConfirmAction,
        handleEditClick,
        handleAvatarClick,
        handleProfilePicModalOk,
        storeImageFile,
        handleConfirmPhotoChoice,
        handleRemovePhotoConfirm,
    };
};
